// VendorShow.jsx
import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Modal, Form, Spinner, Table, Card, Row, Col, Button, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  FaEye, FaArrowLeft, FaStore, FaMoneyBillWave,
  FaPlus, FaTrash, FaSave, FaBook,
  FaCalendarAlt, FaFileInvoice, FaWallet,
  FaTimes, FaCheckCircle, FaClock, FaCreditCard,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaBuilding,
  FaDownload, FaFilter, FaSearch
} from "react-icons/fa";
import Swal from 'sweetalert2';
import PurchaseDetailPanel from "./PurchaseDetailPanel";

export default function VendorShow() {
  const { t } = useContext(TranslatorContext);
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [summary, setSummary] = useState({});
  const [purchases, setPurchases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilter, setShowFilter] = useState(false);

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payments: [{ payment_method_id: "", payment_date: "", amount: "" }],
    notes: ""
  });
  const [paymentErrors, setPaymentErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const AuthToken = localStorage.getItem("authToken");
  const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

  const hasPermission = (perm) => permissions.includes(perm);

  const getTodayDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchVendor();
    fetchPaymentMethods();
  }, [id]);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors/${id}/complete-ledger`, {
        headers: { Authorization: `Bearer ${AuthToken}` }
      });
      setVendor(res.data.vendor);
      setSummary(res.data.summary || {});
      setPurchases(res.data.purchases || []);
      setPayments(res.data.payments || []);
      
      // Sort ledger entries by date (latest first)
      const sortedLedger = [...(res.data.ledger || [])].sort((a, b) => {
        // Convert to timestamp for comparison
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        
        // If dates are equal, sort by type (payments after purchases on same day? or use ID)
        if (dateA === dateB) {
          // For same day, payments should come after purchases
          if (a.type !== b.type) {
            return a.type === 'purchase' ? -1 : 1;
          }
          return b.id - a.id;
        }
        
        // Sort descending (latest first)
        return dateB - dateA;
      });
      
      setLedgerEntries(sortedLedger);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch vendor data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, {
        headers: { Authorization: `Bearer ${AuthToken}` }
      });
      setPaymentMethods(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openPurchasePanel = (purchase) => {
    setSelectedPurchase(purchase);
    setShowSidePanel(true);
  };

  const closePurchasePanel = () => {
    setShowSidePanel(false);
    setSelectedPurchase(null);
  };

  const openAddPaymentModal = () => {
    setPaymentForm({
      payments: [{ payment_method_id: "", payment_date: getTodayDate(), amount: "" }],
      notes: ""
    });
    setPaymentErrors({});
    setShowAddPaymentModal(true);
  };

  const handlePaymentChange = (index, field, value) => {
    const updatedPayments = [...paymentForm.payments];
    updatedPayments[index][field] = value;
    setPaymentForm(prev => ({ ...prev, payments: updatedPayments }));
  };

  const addPaymentRow = () => {
    setPaymentForm(prev => ({
      ...prev,
      payments: [...prev.payments, { payment_method_id: "", payment_date: getTodayDate(), amount: "" }]
    }));
  };

  const removePaymentRow = (index) => {
    if (paymentForm.payments.length === 1) {
      Swal.fire({ toast: true, position: 'top-end', icon: 'warning', title: t("At least one payment row is required"), showConfirmButton: false, timer: 2000 });
      return;
    }
    setPaymentForm(prev => ({ ...prev, payments: prev.payments.filter((_, i) => i !== index) }));
  };

  const handleAddPaymentSubmit = async () => {
    setSubmitting(true);
    setPaymentErrors({});
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/vendors/${id}/payments`, paymentForm, {
        headers: { Authorization: `Bearer ${AuthToken}` }
      });
      if (response.data.status) {
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Payment(s) added successfully!'), showConfirmButton: false, timer: 2000 });
        setShowAddPaymentModal(false);
        fetchVendor();
      }
    } catch (err) {
      if (err.response?.status === 422) {
        setPaymentErrors(err.response.data.errors);
        Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: t('Please check the form for errors'), showConfirmButton: false, timer: 2000 });
      } else {
        Swal.fire({ toast: true, position: 'top-end', icon: 'error', title: t('Failed to add payment'), showConfirmButton: false, timer: 2000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'Rs. 0.00';
    return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 2 }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getPurchaseTypeBadge = (purchaseType) => {
    if (purchaseType === 'regular') {
      return <Badge bg="primary-subtle" className="text-primary px-2 py-1 border border-primary-subtle">Regular</Badge>;
    }
    return <Badge bg="warning-subtle" className="text-warning-emphasis px-2 py-1 border border-warning-subtle">In-House</Badge>;
  };

  const filterLedgerEntries = () => {
    let filtered = [...ledgerEntries];
    if (searchTerm) {
      filtered = filtered.filter(entry =>
        entry.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (dateRange.start) {
      filtered = filtered.filter(entry => entry.date >= dateRange.start);
    }
    if (dateRange.end) {
      filtered = filtered.filter(entry => entry.date <= dateRange.end);
    }
    return filtered;
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Reference #', 'Description', 'Debit', 'Credit', 'Balance'];
    const rows = ledgerEntries.map(entry => [
      entry.date,
      entry.type,
      entry.reference_number,
      entry.description,
      entry.debit,
      entry.credit,
      entry.balance
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor_ledger_${vendor?.name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <PageLayout>
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">{t("loading")}...</p>
        </div>
      </div>
    </PageLayout>
  );

  if (!vendor) return (
    <PageLayout>
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="display-1 mb-4">🔍</div>
          <h4 className="text-muted">{t("Vendor not found")}</h4>
          <Link to="/vendor-list" className="btn btn-primary mt-3">
            <FaArrowLeft className="me-2" /> {t('back to vendors')}
          </Link>
        </div>
      </div>
    </PageLayout>
  );

  const filteredLedger = filterLedgerEntries();

  return (
    <PageLayout>
      <div className="container-fluid px-4 py-4">
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h2 className="mb-1 fw-bold text-dark">
              <FaStore className="text-primary me-2" /> {t('vendor details')}
            </h2>
            <p className="text-muted mb-0">{t('View vendor information and transaction history')}</p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="success" onClick={exportToCSV} className="d-flex align-items-center gap-2">
              <FaDownload /> {t('export')}
            </Button>
            <Link to="/vendor-list" className="btn btn-primary d-flex align-items-center gap-2">
              <FaArrowLeft /> {t('back to vendors')}
            </Link>
          </div>
        </div>

        {/* Vendor Info Card */}
        <Card className="shadow-sm border-0 mb-4 overflow-hidden">
          <Card.Body className="p-0">
            <div className="p-4 border-bottom bg-light">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                    <FaStore className="text-primary fs-3" />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-1 text-dark">{vendor.name}</h3>
                    {vendor.company_name && (
                      <p className="text-muted mb-0 d-flex align-items-center gap-1 small">
                        <FaBuilding size={12} /> {vendor.company_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white">
              <Row className="g-3">
                <Col md={4}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <div className="rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <FaEnvelope className="text-primary" />
                    </div>
                    <div>
                      <p className="text-muted text-uppercase small fw-bold mb-0" style={{ fontSize: '0.75rem' }}>{t('email')}</p>
                      <p className="mb-0 fw-medium text-dark text-break">
                        {vendor.email ? <a href={`mailto:${vendor.email}`} className="text-decoration-none">{vendor.email}</a> : '-'}
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <div className="rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <FaPhone className="text-success" />
                    </div>
                    <div>
                      <p className="text-muted text-uppercase small fw-bold mb-0" style={{ fontSize: '0.75rem' }}>{t('phone')}</p>
                      <p className="mb-0 fw-medium text-dark">
                        {vendor.phone ? <a href={`tel:${vendor.phone}`} className="text-decoration-none">{vendor.phone}</a> : '-'}
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <div className="rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                      <FaMapMarkerAlt className="text-warning" />
                    </div>
                    <div>
                      <p className="text-muted text-uppercase small fw-bold mb-0" style={{ fontSize: '0.75rem' }}>{t('address')}</p>
                      <p className="mb-0 fw-medium text-dark">{vendor.address || '-'}</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>

        {/* Summary Cards */}
        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="d-flex align-items-center justify-content-between p-4">
                <div>
                  <p className="text-muted text-uppercase small fw-bold mb-1" style={{ fontSize: '0.75rem' }}>{t('total purchases')}</p>
                  <h3 className="fw-extrabold mb-0 text-dark">{formatCurrency(summary.total_purchases)}</h3>
                  <small className="text-muted">{summary.total_purchases_count || 0} {t('transactions')}</small>
                </div>
                <div className="rounded-3 p-3 text-primary">
                  <FaStore size={24} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="d-flex align-items-center justify-content-between p-4">
                <div>
                  <p className="text-muted text-uppercase small fw-bold mb-1" style={{ fontSize: '0.75rem' }}>{t('total paid')}</p>
                  <h3 className="fw-extrabold mb-0 text-success">{formatCurrency(summary.total_paid)}</h3>
                  <small className="text-muted">{t('Amount paid to vendor')}</small>
                </div>
                <div className="rounded-3 p-3 text-success">
                  <FaMoneyBillWave size={24} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="d-flex align-items-center justify-content-between p-4">
                <div>
                  <p className="text-muted text-uppercase small fw-bold mb-1" style={{ fontSize: '0.75rem' }}>{t('total balance')}</p>
                  <h3 className="fw-extrabold mb-0 text-warning">{formatCurrency(summary.total_balance)}</h3>
                  <small className="text-muted">{t('Remaining payable')}</small>
                </div>
                <div className="rounded-3 p-3 text-warning">
                  <FaWallet size={24} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Ledger Section */}
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <h5 className="fw-bold mb-0 text-dark">
                <FaBook className="text-primary me-2" /> {t('complete ledger')}
              </h5>
              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowFilter(!showFilter)}
                  className="d-flex align-items-center gap-1 px-3"
                >
                  <FaFilter size={12} /> {t('filter')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={openAddPaymentModal}
                  className="d-flex align-items-center gap-2 px-3"
                >
                  <FaPlus size={12} /> {t('add payment')}
                </Button>
              </div>
            </div>

            {/* Filter Section */}
            {showFilter && (
              <div className="bg-light p-3 rounded-3 mb-4 border">
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-secondary">{t('search')}</Form.Label>
                      <div className="d-flex align-items-center border rounded bg-white px-2">
                        <FaSearch className="text-muted me-2" size={14} />
                        <input
                          type="text"
                          className="form-control border-0 shadow-none py-1.5"
                          placeholder={t('Search reference or details...')}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-secondary">{t('start date')}</Form.Label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateRange.start}
                        onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-secondary">{t('end date')}</Form.Label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateRange.end}
                        onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSearchTerm('');
                        setDateRange({ start: '', end: '' });
                      }}
                      className="w-100 py-2"
                    >
                      {t('clear')}
                    </Button>
                  </Col>
                </Row>
              </div>
            )}

            {filteredLedger.length > 0 ? (
              <div className="table-responsive border rounded">
                <Table hover className="align-middle mb-0">
                  <thead className="table-light text-uppercase font-monospace small">
                    <tr>
                      <th className="ps-3">#</th>
                      <th>{t('date')}</th>
                      <th>{t('type')}</th>
                      <th>{t('reference #')}</th>
                      <th className="text-end">{t('debit (purchase)')}</th>
                      <th className="text-end">{t('credit (payment)')}</th>
                      <th className="text-end pe-3">{t('balance')}</th>
                      <th className="text-center">{t('action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLedger.map((entry, index) => (
                      <tr key={`${entry.type}-${entry.id}-${index}`}>
                        <td className="ps-3 text-muted small">{index + 1}</td>
                        <td className="text-nowrap font-monospace text-secondary">
                          <FaCalendarAlt size={12} className="text-muted me-1.5" />
                          {formatDate(entry.date)}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {entry.type === 'purchase' ? (
                              <>
                                <FaFileInvoice className="text-primary" />
                                <span className="fw-semibold text-dark">{t('Purchase')}</span>
                                {getPurchaseTypeBadge(entry.purchase_type)}
                              </>
                            ) : (
                              <>
                                <FaCreditCard className="text-success" />
                                <span className="fw-semibold text-dark">{t('Payment')}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td><code className="text-primary font-monospace fw-bold">{entry.reference_number || '-'}</code></td>
                        <td className="text-end text-danger fw-bold">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</td>
                        <td className="text-end text-success fw-bold">
                          {entry.credit > 0 ? (
                            <>
                              <div>{formatCurrency(entry.credit)}</div>
                              {entry.description && (
                                <small className="text-muted fw-normal d-block" style={{ fontSize: '0.75rem' }}>
                                  {entry.description}
                                </small>
                              )}
                            </>
                          ) : '-'}
                        </td>
                        <td className="text-end fw-bold text-dark pe-3">{formatCurrency(entry.balance)}</td>
                        <td className="text-center">
                          {entry.type === 'purchase' && (
                            <Button
                              variant="primary"
                              size="sm"
                              className="d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded shadow-sm"
                              onClick={() => {
                                const purchase = purchases.find(
                                  p => p.purchase_number === entry.reference_number
                                );
                                if (purchase) openPurchasePanel(purchase);
                              }}
                            >
                              <FaEye size={12} />
                              <span className="small">{t('view')}</span>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light border-top fw-bold">
                    <tr>
                      <td colSpan="4" className="text-end pe-3">{t('Totals')}:</td>
                      <td className="text-end text-danger">
                        {formatCurrency(filteredLedger.filter(e => e.type === 'purchase').reduce((sum, e) => sum + Number(e.debit || 0), 0))}
                      </td>
                      <td className="text-end text-success">
                        {formatCurrency(filteredLedger.filter(e => e.type === 'payment').reduce((sum, e) => sum + Number(e.credit || 0), 0))}
                      </td>
                      <td className="text-end text-primary pe-3">
                        {formatCurrency(filteredLedger[filteredLedger.length - 1]?.balance || 0)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            ) : (
              <div className="text-center py-5 border rounded bg-light bg-opacity-50">
                <div className="display-4 mb-3">📊</div>
                <h6 className="text-muted mb-0">{t('No ledger entries found')}</h6>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Side Panel for Purchase Details */}
      <PurchaseDetailPanel
        show={showSidePanel}
        onHide={closePurchasePanel}
        purchase={selectedPurchase}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        t={t}
      />

      {/* Add Payment Modal */}
      <Modal show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light px-4 p-2">
          <Modal.Title className="fs-5">
            <div className="d-flex align-items-center gap-2 text-dark">
              <FaMoneyBillWave className="text-primary" />
              <span className="fw-bold">{t('add payment')}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {paymentForm.payments.map((payment, index) => (
            <div key={index} className="mb-3 p-3 border rounded-3 bg-light bg-opacity-50 position-relative">
              <Row className="g-3">
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold text-secondary">{t('payment method')} <span className="text-danger">*</span></Form.Label>
                    <select
                      className="form-select shadow-none"
                      value={payment.payment_method_id}
                      onChange={(e) => handlePaymentChange(index, 'payment_method_id', e.target.value)}
                    >
                      <option value="">{t('select method')}</option>
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                    {paymentErrors[`payments.${index}.payment_method_id`] && (
                      <div className="text-danger small mt-1">{paymentErrors[`payments.${index}.payment_method_id`]}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold text-secondary">{t('amount')} <span className="text-danger">*</span></Form.Label>
                    <input
                      type="number"
                      className="form-control shadow-none"
                      value={payment.amount}
                      step="0.01"
                      onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)}
                      placeholder="0.00"
                    />
                    {paymentErrors[`payments.${index}.amount`] && (
                      <div className="text-danger small mt-1">{paymentErrors[`payments.${index}.amount`]}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold text-secondary">{t('payment date')} <span className="text-danger">*</span></Form.Label>
                    <input
                      type="date"
                      className="form-control shadow-none"
                      value={payment.payment_date}
                      onChange={(e) => handlePaymentChange(index, 'payment_date', e.target.value)}
                    />
                    {paymentErrors[`payments.${index}.payment_date`] && (
                      <div className="text-danger small mt-1">{paymentErrors[`payments.${index}.payment_date`]}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end justify-content-center">
                  {paymentForm.payments.length > 1 && (
                    <Button variant="danger" size="sm" className="mb-1 p-2" onClick={() => removePaymentRow(index)}>
                      <FaTrash size={14} />
                    </Button>
                  )}
                </Col>
              </Row>
            </div>
          ))}

          <div className="text-end mb-3">
            <Button variant="outline-primary" size="sm" onClick={addPaymentRow} className="px-3">
              <FaPlus size={10} className="me-1" /> {t('add another row')}
            </Button>
          </div>

          <Form.Group className="mt-3">
            <Form.Label className="small fw-semibold text-secondary">{t('notes')}</Form.Label>
            <textarea
              className="form-control shadow-none"
              rows="3"
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={t('Optional text details for this entry...')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-light px-4 p-2">
          <Button variant="secondary" size="sm" className="px-3" onClick={() => setShowAddPaymentModal(false)}>{t('cancel')}</Button>
          <Button variant="primary" size="sm" className="px-4" onClick={handleAddPaymentSubmit} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-1" /> {t('save payments')}</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}